import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, EmptyState, PageBody, Pill, SegmentedControl, SkeletonText, useToast } from "@astrum/ui";
import { APPROVAL_LABEL } from "@astrum/shared";
import type { ApprovalStatus } from "@astrum/shared";
import { APPROVAL_TONE } from "../components/TaskApproval.js";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./requests.css";

type Tab = "forms" | "mine" | "approvals";

type FormRow = { id: string; title: string; description: string | null; projectName: string; projectCode: string };
type Submission = {
  id: string;
  formTitle: string;
  projectName: string;
  taskId: string | null;
  taskTitle: string | null;
  taskStatus: string | null;
  approvalStatus: ApprovalStatus | null;
  removed: boolean;
  createdAt: string;
};
type Pending = { taskId: string; title: string; projectCode: string; projectName: string; reporterName: string; requestedAt: string };

/** Forms to fill in, the viewer's own requests, and the tasks waiting for their approval. */
export function Requests() {
  usePageTitle("Requests");
  const [tab, setTab] = useState<Tab>("forms");
  return (
    <PageBody title="Requests" subtitle="Send a request to a project, follow yours, and approve what is waiting for you." width="regular">
      <SegmentedControl
        aria-label="Requests view"
        value={tab}
        onChange={setTab}
        options={[
          { value: "forms", label: "Forms" },
          { value: "mine", label: "My requests" },
          { value: "approvals", label: "Awaiting my approval" },
        ]}
      />
      <div className="requests__body">
        {tab === "forms" && <PagedList<FormRow> path="/forms" listKey="forms" empty="No forms are open for requests yet." render={(f) => (
          <li key={f.id} className="requests__row">
            <Link to={`/forms/${f.id}`} className="requests__title">{f.title}</Link>
            <span className="requests__meta">{f.projectCode} · {f.projectName}</span>
            {f.description && <span className="requests__meta">{f.description}</span>}
          </li>
        )} />}
        {tab === "mine" && <PagedList<Submission> path="/form-submissions" listKey="submissions" empty="You have not sent any requests." render={(s) => (
          <li key={s.id} className="requests__row">
            {s.taskId ? <Link to={`/work/task/${s.taskId}`} className="requests__title">{s.taskTitle}</Link> : <span className="requests__title">{s.formTitle}</span>}
            <span className="requests__meta">{s.formTitle} · {s.projectName} · {new Date(s.createdAt).toLocaleDateString()}</span>
            <span className="requests__pills">
              {s.removed ? <Pill tone="neutral">Removed</Pill> : s.taskStatus && <Pill tone="neutral">{s.taskStatus.replace("_", " ")}</Pill>}
              {s.approvalStatus && <Pill tone={APPROVAL_TONE[s.approvalStatus]}>{APPROVAL_LABEL[s.approvalStatus]}</Pill>}
            </span>
          </li>
        )} />}
        {tab === "approvals" && <PagedList<Pending> path="/approvals" listKey="approvals" empty="Nothing is waiting for your approval." render={(a) => (
          <li key={a.taskId} className="requests__row">
            <Link to={`/work/task/${a.taskId}`} className="requests__title">{a.title}</Link>
            <span className="requests__meta">{a.projectCode} · {a.projectName} · from {a.reporterName}</span>
          </li>
        )} />}
      </div>
    </PageBody>
  );
}

function PagedList<T>({ path, listKey, empty, render }: { path: string; listKey: string; empty: string; render: (row: T) => React.ReactNode }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<T[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);

  const load = useCallback(
    async (before?: string) => {
      try {
        const res = await api.get<Record<string, unknown>>(`${path}${before ? `?before=${encodeURIComponent(before)}` : ""}`);
        const page = (res[listKey] as T[]) ?? [];
        setRows((prev) => (before ? [...(prev ?? []), ...page] : page));
        setCursor((res.nextCursor as string | null) ?? null);
      } catch (e) {
        setRows((prev) => prev ?? []);
        toast(e instanceof ApiError ? e.message : "Could not load the list.", { tone: "danger" });
      }
    },
    [path, listKey, toast],
  );

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  if (rows === null) return <SkeletonText lines={3} />;
  if (rows.length === 0) return <EmptyState icon="document" size="inline" title={empty} />;
  return (
    <>
      <ul className="requests__list">{rows.map(render)}</ul>
      {cursor && (
        <Button size="sm" variant="secondary" onClick={() => void load(cursor)}>
          Show more
        </Button>
      )}
    </>
  );
}
